import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { CardapioAnalyticsService } from '../analytics/cardapio-analytics.service';
import { ConstantsService } from '../fidelidade/ConstantsService';

@Component({
  selector: 'app-tempo-real-cardapio',
  templateUrl: './tempo-real-cardapio.component.html',
  styleUrls: ['./tempo-real-cardapio.component.scss']
})
export class TempoRealCardapioComponent implements OnInit, OnDestroy {
  readonly intervaloAtualizacaoMs = 15000;
  readonly janelaMinutos = 30;
  readonly filtrosUsuarios = [
    { key: 'todos', label: 'Todos' },
    { key: 'navegando', label: 'Navegando' },
    { key: 'produtos', label: 'Vendo produtos' },
    { key: 'sacola', label: 'Na sacola' },
    { key: 'finalizando', label: 'Finalizando' }
  ];

  empresa: any = null;
  dados: any = this.crieDadosVazios();
  status: 'carregando' | 'pronto' | 'sem_projeto' | 'erro' = 'carregando';
  carregando = false;
  atualizacaoFalhou = false;
  erroMensagem = '';
  listaUsuariosAberta = false;
  visitanteSelecionado: any = null;
  filtroUsuariosEtapa = 'todos';
  buscaUsuarios = '';

  @ViewChild('painelUsuarios') private painelUsuarios: ElementRef<HTMLElement>;
  @ViewChild('botaoFecharUsuarios') private botaoFecharUsuarios: ElementRef<HTMLButtonElement>;
  @ViewChild('botaoVoltarUsuarios') private botaoVoltarUsuarios: ElementRef<HTMLButtonElement>;

  private empresaSub: Subscription;
  private atualizador: any;
  private focoAnterior: HTMLElement = null;
  private focoListaUsuariosAnterior: HTMLElement = null;
  private overflowPaginaAnterior = '';
  private rolagemPaginaBloqueada = false;

  constructor(
    private readonly constantsService: ConstantsService,
    private readonly analyticsService: CardapioAnalyticsService
  ) {}

  ngOnInit(): void {
    this.empresaSub = this.constantsService.empresa$.subscribe((empresa: any) => {
      if (!empresa?.id) return;
      this.empresa = empresa;
      this.status = 'carregando';
      this.carregue();
      this.inicieAtualizacaoAutomatica();
    });
  }

  ngOnDestroy(): void {
    if (this.empresaSub) this.empresaSub.unsubscribe();
    if (this.atualizador) clearInterval(this.atualizador);
    this.restaureRolagemPagina();
  }

  async carregue(): Promise<void> {
    if (this.carregando || !this.empresa?.id) return;
    this.carregando = true;
    this.erroMensagem = '';

    try {
      const dados = await this.analyticsService.obtenhaTempoReal(this.janelaMinutos);
      const idVisitanteSelecionado = this.visitanteSelecionado?.idPresenca;
      this.dados = dados || this.crieDadosVazios();
      if (idVisitanteSelecionado) {
        this.visitanteSelecionado = (this.dados?.visitantesAtivos || [])
          .find((visitante: any) => visitante?.idPresenca === idVisitanteSelecionado) || null;
      }
      this.atualizacaoFalhou = false;
      this.status = this.dados?.metadados?.status === 'sem_projeto' ? 'sem_projeto' : 'pronto';
    } catch (erro: any) {
      this.erroMensagem = typeof erro === 'string' ? erro : 'Não foi possível atualizar os dados em tempo real.';
      this.atualizacaoFalhou = true;
      if (this.status !== 'pronto') this.status = 'erro';
    } finally {
      this.carregando = false;
    }
  }

  formateMoeda(valor: any): string {
    const numero = Number(valor || 0);
    return numero.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  }

  formateNumero(valor: any): string {
    return Number(valor || 0).toLocaleString('pt-BR', { maximumFractionDigits: 2 });
  }

  formateTempoRelativo(valor: string): string {
    const timestamp = valor ? new Date(valor).getTime() : NaN;
    if (Number.isNaN(timestamp)) return 'agora';

    const segundos = Math.max(0, Math.floor((Date.now() - timestamp) / 1000));
    if (segundos < 10) return 'agora';
    if (segundos < 60) return `há ${segundos}s`;

    const minutos = Math.floor(segundos / 60);
    if (minutos < 60) return `há ${minutos} min`;
    return `há ${Math.floor(minutos / 60)}h`;
  }

  obtenhaTextoAtualizacao(): string {
    return this.formateTempoRelativo(this.dados?.metadados?.consultadoEm);
  }

  obtenhaTextoAlertaPagamento(): string {
    const total = Number(this.dados?.alertaPagamento?.total || 0);
    const tempo = this.formateTempoRelativo(this.dados?.alertaPagamento?.ultimoEm);

    if (total === 1) return `Um cliente encontrou dificuldade no pagamento ${tempo}`;
    return `${total} clientes encontraram dificuldade no pagamento — o último ${tempo}`;
  }

  obtenhaVisitantesDaEtapa(etapa: string): any[] {
    return (this.dados?.visitantesAtivos || []).filter((visitante: any) => visitante?.etapa === etapa);
  }

  obtenhaVisitantesVisiveis(etapa: string): any[] {
    return this.obtenhaVisitantesDaEtapa(etapa).slice(0, 3);
  }

  obtenhaQuantidadeVisitantesOcultos(etapa: string): number {
    return Math.max(0, this.obtenhaVisitantesDaEtapa(etapa).length - 3);
  }

  abraListaUsuarios(etapa: string = 'todos'): void {
    if (!(this.dados?.visitantesAtivos || []).length) return;

    this.filtroUsuariosEtapa = etapa;
    this.buscaUsuarios = '';
    this.visitanteSelecionado = null;

    if (!this.listaUsuariosAberta) {
      this.listaUsuariosAberta = true;
      this.bloqueieRolagemPagina();

      setTimeout(() => {
        if (this.botaoFecharUsuarios?.nativeElement) {
          this.botaoFecharUsuarios.nativeElement.focus();
        }
      });
    }
  }

  fecheListaUsuarios(): void {
    if (!this.listaUsuariosAberta) return;

    this.listaUsuariosAberta = false;
    this.visitanteSelecionado = null;
    this.focoListaUsuariosAnterior = null;
    this.restaureRolagemPagina();

    const focoAnterior = this.focoAnterior;
    this.focoAnterior = null;
    setTimeout(() => focoAnterior?.focus());
  }

  selecioneFiltroUsuarios(etapa: string): void {
    this.filtroUsuariosEtapa = etapa;
  }

  aoBuscarUsuarios(evento: Event): void {
    this.buscaUsuarios = String((evento.target as HTMLInputElement)?.value || '');
  }

  abraHistoricoUsuario(visitante: any): void {
    if (!visitante?.idPresenca) return;

    this.focoListaUsuariosAnterior = typeof document !== 'undefined' && document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    this.visitanteSelecionado = visitante;

    setTimeout(() => this.botaoVoltarUsuarios?.nativeElement?.focus());
  }

  volteParaListaUsuarios(): void {
    if (!this.visitanteSelecionado) return;

    this.visitanteSelecionado = null;
    const focoAnterior = this.focoListaUsuariosAnterior;
    this.focoListaUsuariosAnterior = null;
    setTimeout(() => focoAnterior?.focus());
  }

  obtenhaAcoesVisitante(visitante: any = this.visitanteSelecionado): any[] {
    return Array.isArray(visitante?.acoes) ? visitante.acoes : [];
  }

  obtenhaNomeCompletoVisitante(visitante: any): string {
    const nome = String(visitante?.nomeExibicao || '').trim();
    return visitante?.identificado && nome ? nome : 'Visitante anônimo';
  }

  obtenhaUsuariosFiltrados(): any[] {
    const termo = this.normalizeBusca(this.buscaUsuarios);

    return [...(this.dados?.visitantesAtivos || [])]
      .filter((visitante: any) => {
        if (this.filtroUsuariosEtapa !== 'todos' && visitante?.etapa !== this.filtroUsuariosEtapa) return false;
        if (!termo) return true;

        const nome = visitante?.identificado && visitante?.nomeExibicao
          ? visitante.nomeExibicao
          : 'Visitante anônimo';
        return this.normalizeBusca(nome).includes(termo);
      })
      .sort((a: any, b: any) => this.obtenhaTimestampVisitante(b) - this.obtenhaTimestampVisitante(a));
  }

  obtenhaTotalFiltroUsuarios(etapa: string): number {
    if (etapa === 'todos') return (this.dados?.visitantesAtivos || []).length;
    return this.obtenhaVisitantesDaEtapa(etapa).length;
  }

  obtenhaLabelEtapa(etapa: string): string {
    return this.filtrosUsuarios.find((filtro: any) => filtro.key === etapa)?.label || 'Navegando';
  }

  @HostListener('document:keydown', ['$event'])
  aoPressionarTeclaListaUsuarios(evento: KeyboardEvent): void {
    if (!this.listaUsuariosAberta) return;

    if (evento.key === 'Escape') {
      evento.preventDefault();
      this.fecheListaUsuarios();
      return;
    }

    if (evento.key !== 'Tab' || !this.painelUsuarios?.nativeElement) return;

    const focaveis = Array.from(this.painelUsuarios.nativeElement.querySelectorAll<HTMLElement>(
      'button:not([disabled]), input:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
    )).filter((elemento: HTMLElement) => elemento.offsetParent !== null);

    if (!focaveis.length) return;

    const primeiro = focaveis[0];
    const ultimo = focaveis[focaveis.length - 1];
    if (evento.shiftKey && document.activeElement === primeiro) {
      evento.preventDefault();
      ultimo.focus();
    } else if (!evento.shiftKey && document.activeElement === ultimo) {
      evento.preventDefault();
      primeiro.focus();
    }
  }

  obtenhaNomeVisitante(visitante: any): string {
    const nome = String(visitante?.nomeExibicao || '').trim();
    return visitante?.identificado && nome ? nome.split(/\s+/)[0] : 'Anônimo';
  }

  obtenhaIniciaisVisitante(visitante: any): string {
    if (!visitante?.identificado) return '';

    return String(visitante?.nomeExibicao || '?')
      .split(/\s+/)
      .filter((parte: string) => !!parte)
      .slice(0, 2)
      .map((parte: string) => parte.charAt(0).toUpperCase())
      .join('');
  }

  obtenhaDescricaoVisitante(visitante: any, etapa: string): string {
    const nome = visitante?.identificado && visitante?.nomeExibicao
      ? visitante.nomeExibicao
      : 'Visitante anônimo';
    const atividade = visitante?.ultimaAtividadeEm
      ? this.formateTempoRelativo(visitante.ultimaAtividadeEm)
      : 'aguardando primeira ação';

    return `${nome}. ${etapa}. Última atividade: ${atividade}.`;
  }

  formateTelefoneVisitante(visitante: any): string {
    const telefone = String(visitante?.telefone || '').replace(/\D/g, '');
    const codigoPais = String(visitante?.codigoPais || '55').replace(/\D/g, '');
    if (!telefone) return '';

    const nacional = codigoPais === '55' && telefone.startsWith('55') && telefone.length > 11
      ? telefone.slice(2)
      : telefone;

    if (codigoPais === '55' && (nacional.length === 10 || nacional.length === 11)) {
      const ddd = nacional.slice(0, 2);
      const numero = nacional.slice(2);
      const separador = numero.length === 9 ? 5 : 4;
      return `+55 (${ddd}) ${numero.slice(0, separador)}-${numero.slice(separador)}`;
    }

    return `${codigoPais ? `+${codigoPais} ` : ''}${nacional}`;
  }

  formateHorario(valor: string): string {
    const data = valor ? new Date(valor) : null;
    if (!data || Number.isNaN(data.getTime())) return '--:--';

    return data.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  }

  obtenhaLinkWhatsapp(visitante: any): string | null {
    const telefone = String(visitante?.telefone || '').replace(/\D/g, '');
    const codigoPais = String(visitante?.codigoPais || '55').replace(/\D/g, '');
    if (!telefone) return null;

    const numero = codigoPais && telefone.startsWith(codigoPais) && telefone.length > 11
      ? telefone
      : `${codigoPais}${telefone}`;
    return `https://wa.me/${numero}`;
  }

  obtenhaResumoIdentificacao(): string {
    const total = Number(this.dados?.visitantesAgora || 0);
    if (!total) return 'aguardando visitas';

    const anonimos = Number(this.dados?.resumo?.acessosAnonimos || 0);
    const identificados = Math.max(0, total - anonimos);
    const partes: string[] = [];

    if (identificados) partes.push(`${identificados} ${identificados === 1 ? 'identificado' : 'identificados'}`);
    if (anonimos) partes.push(`${anonimos} ${anonimos === 1 ? 'anônimo' : 'anônimos'}`);
    return partes.join(' · ');
  }

  obtenhaIconeEtapa(key: string): string {
    const icones: any = {
      navegando: 'fas fa-compass',
      produtos: 'far fa-eye',
      sacola: 'fas fa-shopping-bag',
      finalizando: 'fas fa-check-circle'
    };
    return icones[key] || 'fas fa-circle';
  }

  obtenhaIconeAtividade(tipo: string): string {
    const icones: any = {
      visita: 'fas fa-sign-in-alt',
      produto: 'far fa-eye',
      busca: 'fas fa-search',
      sacola: 'fas fa-shopping-bag',
      checkout: 'far fa-credit-card',
      alerta: 'fas fa-exclamation',
      pedido: 'fas fa-check'
    };
    return icones[tipo] || 'fas fa-circle';
  }

  obtenhaNomeAutorAtividade(atividade: any): string {
    const nome = String(atividade?.autor?.nomeExibicao || '').trim();
    return atividade?.autor?.identificado && nome ? nome.split(/\s+/)[0] : 'Anônimo';
  }

  obtenhaIniciaisProduto(nome: string): string {
    return String(nome || '?')
      .split(/\s+/)
      .filter((parte: string) => !!parte)
      .slice(0, 2)
      .map((parte: string) => parte.charAt(0).toUpperCase())
      .join('');
  }

  aoFalharImagem(produto: any): void {
    if (produto) produto.imagem = null;
  }

  houveMovimento(): boolean {
    return Number(this.dados?.metadados?.totalEventos || 0) > 0;
  }

  trackByEtapa(_index: number, etapa: any): string {
    return etapa?.key;
  }

  trackByAtividade(index: number, atividade: any): string {
    return `${atividade?.ocorridoEm || index}-${atividade?.texto || ''}`;
  }

  trackByVisitante(index: number, visitante: any): string {
    return String(visitante?.idPresenca || index);
  }

  trackByProduto(_index: number, produto: any): string {
    return String(produto?.id || produto?.nome || '');
  }

  trackByMinuto(index: number, minuto: any): string {
    return String(minuto?.inicio || index);
  }

  private inicieAtualizacaoAutomatica(): void {
    if (this.atualizador) clearInterval(this.atualizador);
    this.atualizador = setInterval(() => {
      if (typeof document === 'undefined' || !document.hidden) this.carregue();
    }, this.intervaloAtualizacaoMs);
  }

  private normalizeBusca(valor: any): string {
    return String(valor || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toLowerCase();
  }

  private obtenhaTimestampVisitante(visitante: any): number {
    const timestamp = new Date(visitante?.ultimaAtividadeEm || visitante?.atualizadoEm || 0).getTime();
    return Number.isFinite(timestamp) ? timestamp : 0;
  }

  private bloqueieRolagemPagina(): void {
    if (typeof document === 'undefined') return;

    this.focoAnterior = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    this.overflowPaginaAnterior = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    this.rolagemPaginaBloqueada = true;
  }

  private restaureRolagemPagina(): void {
    if (typeof document === 'undefined' || !this.rolagemPaginaBloqueada) return;
    document.body.style.overflow = this.overflowPaginaAnterior;
    this.rolagemPaginaBloqueada = false;
  }

  private crieDadosVazios(): any {
    return {
      visitantesAgora: 0,
      visitantesAtivos: [],
      resumo: {
        itensSacolas: 0,
        valorSacolas: 0,
        sacolasAtivas: 0,
        emFechamento: 0,
        pedidos: 0,
        ultimoPedidoEm: null,
        clientesIdentificados: 0,
        acessosAnonimos: 0
      },
      etapas: [],
      movimento: [],
      atividades: [],
      produtos: [],
      alertaPagamento: { total: 0, ultimoEm: null },
      oportunidade: {
        sacolasParadas: 0,
        valorSacolasParadas: 0,
        minutosSemAtividade: 10
      },
      metadados: {
        status: 'ok',
        consultadoEm: null,
        totalEventos: 0
      }
    };
  }
}
